import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, shareReplay } from 'rxjs';
import { AppConfigService } from '../app-config.service';
import { StubAccessService } from '../core/access';
import { ConfigurationInterface, WhitelabelService } from '@galaxy/partner';
import { CrmAccessService, ObjectType } from '@galaxy/crm/static';
import { Views } from '../core/access/interface';

@Injectable({ providedIn: 'root' })
export class PartnerCRMAccessService implements CrmAccessService {
  private readonly appConfigService = inject(AppConfigService);
  private readonly accessService = inject(StubAccessService);
  private readonly whitelabelService = inject(WhitelabelService);

  private readonly cachedMarketConfig = new Map<string, Observable<ConfigurationInterface>>();
  private readonly viewAccess$ = this.accessService
    .hasAccessToViews([
      Views.partnerAll,
      Views.salespersonAll,
      Views.viewAllCampaigns,
      Views.taskManager,
      Views.automations,
      Views.editAutomations,
      Views.crmContactEdit,
      Views.crmCompanyEdit,
      Views.inboxFeature,
      Views.customObjectView,
    ])
    .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

  readonly hasPartnerPermissions$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.partnerAll)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly hasSalespersonPermissions$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.salespersonAll)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canViewAllCampaigns$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.viewAllCampaigns)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canAccessTaskManager$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.taskManager)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canAccessSales$ = combineLatest([this.hasSalespersonPermissions$, this.appConfigService.config$]).pipe(
    map(([salespersonView, config]) => config.isSuperAdmin || config.userCanAccessSales || salespersonView),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canAccessAutomations$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.automations)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canModifyAutomations$ = this.viewAccess$.pipe(
    map((response) => response.get(Views.editAutomations)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canAccessInbox$ = this.viewAccess$.pipe(map((response) => response.get(Views.inboxFeature)));

  canAccessProductPage$(partnerId: string, marketId: string): Observable<boolean> {
    return combineLatest([
      this.appConfigService.config$,
      this.hasPartnerPermissions$,
      this.marketWideAccountAccess$(partnerId, marketId),
    ]).pipe(
      map(([config, adminView, marketWideAccess]) => config?.isSuperAdmin || adminView || marketWideAccess),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  canAccessCrmObjectType$(objectType: ObjectType): Observable<boolean> {
    switch (objectType) {
      case 'Contact':
        return this.viewAccess$.pipe(
          map((response) => response.get(Views.crmContactEdit)),
          shareReplay({ refCount: true, bufferSize: 1 }),
        );
      case 'Company':
        return this.viewAccess$.pipe(
          map((response) => response.get(Views.crmCompanyEdit)),
          shareReplay({ refCount: true, bufferSize: 1 }),
        );
      case 'CustomObject':
        return this.viewAccess$.pipe(
          map((response) => response.get(Views.customObjectView)),
          shareReplay({ refCount: true, bufferSize: 1 }),
        );
      default:
        return of(false);
    }
  }

  private getMarketConfig$(partnerId: string, marketId: string): Observable<ConfigurationInterface> {
    const key = `${partnerId}:${marketId}`;
    if (!this.cachedMarketConfig.has(key)) {
      const config$ = this.whitelabelService
        .getConfiguration(partnerId, marketId)
        .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
      this.cachedMarketConfig.set(key, config$);
    }
    return this.cachedMarketConfig.get(key);
  }

  private marketWideAccountAccess$(partnerId: string, marketId: string): Observable<boolean> {
    return this.getMarketConfig$(partnerId, marketId).pipe(
      map((config) => config.salesConfiguration?.stSalespersonSharedAccountAccess || false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }
}
